/**
 * Admin billing-task endpoints (GDN-2644), hand-written because they are
 * not in the deployed OpenAPI yet (same precedent as
 * usePresignRawUploadsMutation in hooks.ts). Swap to the generated
 * `billing.ts` client after the backend PR deploys, if desired.
 *
 * Task item/list types are shared with the provider Tasks tab via a
 * direct file import (no feature barrel — avoids autoRegisterFeature
 * side effects).
 */
import type {
  BillingTaskCategory,
  BillingTaskListResponse,
  BillingTaskStatus,
  BillingTaskSubjectType,
} from '@features/billing/store/tasks-api';
import { baseApi } from '@store/baseApi';

export type {
  BillingTaskCategory,
  BillingTaskItem,
  BillingTaskListResponse,
  BillingTaskSegment,
  BillingTaskStatus,
  BillingTaskSubjectType,
} from '@features/billing/store/tasks-api';

export interface OrgOpenTaskCountsItem {
  organization_id: string;
  organization_name?: string | null;
  open_count: number;
  in_progress_count: number;
  unfinished_count: number;
  oldest_unfinished_created_at?: string | null;
}

export interface OrgOpenTaskCountsResponse {
  items: OrgOpenTaskCountsItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AdminOrgBillingTasksArgs {
  organizationId: string;
  page?: number;
  perPage?: number;
  status?: BillingTaskStatus;
  category?: BillingTaskCategory;
  subjectType?: BillingTaskSubjectType;
}

function compactParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  ) as Partial<T>;
}

const adminBillingTasksApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getAdminBillingTaskOrgs: build.query<OrgOpenTaskCountsResponse, void>({
      query: () => ({ url: '/v2/admin/billing/tasks/organizations' }),
    }),
    getAdminOrgBillingTasks: build.query<BillingTaskListResponse, AdminOrgBillingTasksArgs>({
      query: ({ organizationId, page, perPage, status, category, subjectType }) => ({
        url: `/v2/admin/billing/tasks/organizations/${organizationId}`,
        params: compactParams({
          page,
          per_page: perPage,
          status,
          category,
          subject_type: subjectType,
        }),
      }),
    }),
  }),
});

export const {
  useGetAdminBillingTaskOrgsQuery: useBillingTaskOrgsQuery,
  useGetAdminOrgBillingTasksQuery: useOrgBillingTasksQuery,
} = adminBillingTasksApi;
